import { DENOMINATIONS, formatDate, formatDateTime, formatINR } from "@templeos/shared";
import { api } from "./api.js";
import type { Payment, QueuedSale, SaleLine, Ticket } from "./types.js";
import { needsRaster, rasterize } from "./raster.js";

/** Device agent print job (CONTRACTS.md, T1b device agent POST /print). */
export interface PrintLine { text: string; align?: "left" | "center" | "right"; bold?: boolean; size?: 1 | 2 }
export interface PrintRaster { width: 384 | 576; height: number; dataBase64: string }
export interface PrintJob { copies?: number; title?: string; lines: PrintLine[]; raster?: PrintRaster; qr?: string; barcode?: string; cut?: boolean; drawer?: boolean }

export interface SlipContext {
  templeName: string;
  trustName?: string | null;
  counter: string;
  cashier: string;
  paperWidth: 42 | 32;          // characters per line: 80 mm or 58 mm rolls
  webUrl?: string | null;       // public site, for the receipt verification QR
}

export interface Duplicate { reason: string; approvedBy?: string | null }

const c = (text: string, bold = false, size: 1 | 2 = 1): PrintLine => ({ text, align: "center", bold, size });
const l = (text: string, bold = false): PrintLine => ({ text, bold });
const rule = (w: number): PrintLine => l("-".repeat(w));

/** "Label ....... value" padded to the paper width. Slips are plain ASCII, so what is measured is what prints. */
export function cols(left: string, right: string, width: number): string {
  const room = Math.max(1, width - right.length - 1);
  const lft = left.length > room ? `${left.slice(0, room - 1)}.` : left;
  return `${lft}${" ".repeat(Math.max(1, width - lft.length - right.length))}${right}`;
}

/** Thermal printers have no rupee glyph (the agent prints "Rs."), so slips spell it out before measuring widths. */
const money = (p: number) => `${p < 0 ? "-" : ""}Rs.${formatINR(Math.abs(p), { symbol: false })}`;
const payText = (ps: readonly Payment[]) => (ps.length ? ps.map((p) => `${p.mode.toUpperCase()} ${money(p.amount)}`).join(" + ") : "FREE");

function head(ctx: SlipContext, dup?: Duplicate): PrintLine[] {
  const out = [c(ctx.templeName, true, 2)];
  if (ctx.trustName) out.push(c(ctx.trustName));
  if (dup) out.push(c("*** DUPLICATE ***", true, 2), c(`Reprint: ${dup.reason}${dup.approvedBy ? ` / appr. ${dup.approvedBy}` : ""}`));
  return out;
}

function foot(sale: Pick<QueuedSale, "seq" | "createdAt" | "number" | "clientRef">, ctx: SlipContext): PrintLine[] {
  return [
    l(cols("Receipt", sale.number ?? "PENDING SYNC", ctx.paperWidth)),
    l(cols("Ref", sale.clientRef, ctx.paperWidth)),
    l(cols(`Roll #${String(sale.seq).padStart(5, "0")}`, formatDateTime(sale.createdAt), ctx.paperWidth)),
    l(cols(`Counter ${ctx.counter}`, ctx.cashier, ctx.paperWidth)),
  ];
}

/** One slip per ticket, each with its own QR and barcode, then one receipt for any sevas, prasad or donations. */
export function saleJobs(sale: QueuedSale, ctx: SlipContext, dup?: Duplicate): PrintJob[] {
  const jobs: PrintJob[] = [];
  const ticketLines = sale.lines.filter((x) => x.kind === "ticket");
  const tickets: (Ticket & { price: number })[] = [];
  const issued = [...(sale.tickets ?? [])]; // the server issues ticket codes in bill-line order
  for (const line of ticketLines) {
    for (let i = 0; i < line.qty; i++) {
      const t = issued.shift();
      tickets.push({ code: t?.code ?? "", itemName: line.name, serviceDate: t?.serviceDate ?? line.serviceDate ?? null, slot: t?.slot ?? line.slot ?? null, price: line.unitPrice });
    }
  }
  tickets.forEach((t, i) => {
    const provisional = !t.code;
    const code = t.code || `${sale.clientRef}/${i + 1}`;
    jobs.push({
      title: `Ticket ${code}`,
      lines: [
        ...head(ctx, dup),
        rule(ctx.paperWidth),
        c(t.itemName.toUpperCase(), true, 2),
        c(`${t.serviceDate ? formatDate(t.serviceDate) : formatDate(sale.day)}${t.slot ? `   ${t.slot}` : ""}`, true),
        c(t.price ? money(t.price) : "FREE ENTRY", true),
        c(`Ticket ${i + 1} of ${tickets.length}`),
        ...(provisional ? [c("OFFLINE TICKET - show receipt at gate", true)] : []),
        rule(ctx.paperWidth),
        ...foot(sale, ctx),
        l(cols("Paid", payText(sale.payments), ctx.paperWidth)),
        c(provisional ? code : t.code, true),
        c("Valid for one entry. Keep until you leave."),
      ],
      qr: code,
      barcode: code,
      cut: true,
    });
  });
  const others = sale.lines.filter((x) => x.kind !== "ticket");
  if (others.length || !tickets.length) jobs.push(receiptJob(sale, others.length ? others : sale.lines, ctx, dup));
  if (jobs[0] && sale.payments.some((p) => p.mode === "cash")) jobs[0] = { ...jobs[0], drawer: true };
  return jobs;
}

function receiptJob(sale: QueuedSale, lines: readonly SaleLine[], ctx: SlipContext, dup?: Duplicate): PrintJob {
  const w = ctx.paperWidth;
  const link = sale.publicToken && ctx.webUrl ? `${ctx.webUrl.replace(/\/$/, "")}/r/${sale.publicToken}` : sale.number ?? sale.clientRef;
  return {
    title: `Receipt ${sale.number ?? sale.clientRef}`,
    lines: [
      ...head(ctx, dup),
      c("RECEIPT", true),
      rule(w),
      ...lines.flatMap((x) => [l(x.name), l(cols(`  ${x.qty} x ${money(x.unitPrice)}`, money(x.amount), w))]),
      rule(w),
      l(cols("TOTAL", money(sale.total), w), true),
      ...sale.payments.map((p) => l(cols(`  ${p.mode.toUpperCase()}${p.reference ? ` ${p.reference}` : ""}`, money(p.amount), w))),
      rule(w),
      ...foot(sale, ctx),
      ...(lines.some((x) => x.kind === "donation") ? [c(sale.status === "synced"
        ? "80G: see final donation receipt"
        : "80G: pending server verification")] : []),
      c("Scan to verify this receipt"),
    ],
    qr: link,
    barcode: sale.number ?? sale.clientRef,
    cut: true,
  };
}

/** status: issued or cancelled (server), pending (on this terminal only) or rejected (refused by the server). */
export interface AuditRow { createdAt: string; number: string; total: number; modes: string; status: string }

const TAG: Record<string, string> = { cancelled: " VOID", pending: " WAIT", rejected: " REFUSED" };
const sum = (rows: readonly AuditRow[]) => rows.reduce((s, r) => s + r.total, 0);

/**
 * Thermal mini audit slip of the cashier's own transactions (SOW Module 4). Bills waiting to sync count as sales
 * (the money is in the drawer) and are shown separately; void and refused bills never count.
 */
export function auditSlipJob(rows: readonly AuditRow[], ctx: SlipContext, filterLabel: string): PrintJob {
  const w = ctx.paperWidth;
  const of = (status: string) => rows.filter((r) => r.status === status);
  const sold = rows.filter((r) => r.status === "issued" || r.status === "pending");
  const byMode = new Map<string, number>();
  for (const r of sold) byMode.set(r.modes || "free", (byMode.get(r.modes || "free") ?? 0) + r.total);
  return {
    title: "Mini audit slip",
    lines: [
      ...head(ctx), c("MINI AUDIT SLIP", true), c(filterLabel), rule(w),
      ...rows.map((r) => l(cols(`${formatDateTime(r.createdAt).slice(-8)} ${r.number}${TAG[r.status] ?? ""}`, money(r.total), w))),
      rule(w),
      ...[...byMode].map(([m, v]) => l(cols(m.toUpperCase(), money(v), w))),
      l(cols(`Bills ${sold.length}  Void ${of("cancelled").length}`, money(sum(sold)), w), true),
      ...(of("pending").length ? [l(cols(`  incl. waiting to sync ${of("pending").length}`, money(sum(of("pending"))), w))] : []),
      ...(of("rejected").length ? [l(cols(`Refused, not counted ${of("rejected").length}`, money(sum(of("rejected"))), w))] : []),
      l(cols(`Counter ${ctx.counter}`, ctx.cashier, w)),
      l(`Printed ${formatDateTime(new Date())}`),
    ],
    cut: true,
  };
}

export interface HandoverData {
  openedAt: string; openingFloat: number; expected: number; counted: number; variance: number; explanation?: string | null;
  denominations: Record<string, number>; receipts: number; byMode: { mode: string; amount: number }[]; approvedBy?: string | null;
}

/** Shift closure handover slip (SOW Module 4 output), signed by the cashier and the supervisor. */
export function handoverSlipJob(d: HandoverData, ctx: SlipContext): PrintJob {
  const w = ctx.paperWidth;
  return {
    title: "Shift handover slip",
    lines: [
      ...head(ctx), c("SHIFT HANDOVER SLIP", true), rule(w),
      l(cols("Opened", formatDateTime(d.openedAt), w)), l(cols("Closed", formatDateTime(new Date()), w)),
      l(cols("Bills issued", String(d.receipts), w)),
      ...d.byMode.map((m) => l(cols(`Collected ${m.mode.toUpperCase()}`, money(m.amount), w))),
      rule(w),
      ...DENOMINATIONS.filter((x) => (d.denominations[String(x.paise)] ?? 0) > 0)
        .map((x) => l(cols(`${x.label.replace("₹", "Rs.")} x ${d.denominations[String(x.paise)]}`, money(x.paise * (d.denominations[String(x.paise)] ?? 0)), w))),
      rule(w),
      l(cols("Opening float", money(d.openingFloat), w)),
      l(cols("Expected cash", money(d.expected), w)),
      l(cols("Counted cash", money(d.counted), w), true),
      l(cols("Variance", money(d.variance), w), true),
      ...(d.variance !== 0 ? [l(`Reason: ${d.explanation ?? ""}`), l(`Approved by: ${d.approvedBy ?? ""}`)] : []),
      l(""), l("Cashier sign:    ______________"), l(""), l("Supervisor sign: ______________"),
      l(cols(`Counter ${ctx.counter}`, ctx.cashier, w)),
    ],
    cut: true,
    drawer: true,
  };
}

// ---- sending
/** Long enough for a slow ESC/POS printer: a timeout after the slip printed would be reported as a failure. */
const AGENT_TIMEOUT_MS = 5_000;
let preparationError: string | null = null;

export async function sendToAgent(baseUrl: string, job: PrintJob): Promise<boolean> {
  let submitted = false;
  try {
    const logo = (import.meta.env.VITE_RECEIPT_LOGO_URL as string | undefined) ?? "";
    if (needsRaster(job, logo)) {
      const health = await fetch(`${baseUrl.replace(/\/$/, "")}/health`, { signal: AbortSignal.timeout(1500) });
      const capabilities = await health.json() as { paperMm?: number; raster?: { version?: number } };
      if (!health.ok || capabilities.raster?.version !== 1) throw new Error("Update the device agent to print Hindi/Gujarati text or a receipt logo.");
      try { job = await rasterize(job, capabilities.paperMm ?? 0, logo); }
      catch (err) { preparationError = (err as Error).message; return false; }
    }
    submitted = true;
    const res = await fetch(`${baseUrl.replace(/\/$/, "")}/print`, {
      method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(job), signal: AbortSignal.timeout(AGENT_TIMEOUT_MS),
    });
    return res.ok && ((await res.json()) as { ok?: boolean }).ok === true;
  } catch (err) {
    if (submitted) preparationError = "Printer outcome is unknown. Check the printer and collected slips before requesting a supervised reprint; no browser copy was sent.";
    else preparationError = err instanceof Error ? err.message : "Could not prepare this slip for printing.";
    return false;
  }
}

export async function agentOnline(baseUrl: string): Promise<boolean> {
  try {
    const res = await fetch(`${baseUrl.replace(/\/$/, "")}/health`, { signal: AbortSignal.timeout(1_500) });
    return res.ok;
  } catch {
    return false;
  }
}

type BrowserPrinter = (jobs: PrintJob[]) => Promise<void>;
let browserPrinter: BrowserPrinter | null = null;
/** The print area component registers the window.print fallback (80 mm layout). */
export const setBrowserPrinter = (fn: BrowserPrinter | null): void => { browserPrinter = fn; };

export interface PrintOutcome { via: "agent" | "browser" | "none"; printed: number; total: number }

/**
 * Print through the device agent, counting every slip it confirms. Only when the agent is not running at all does the
 * browser's print dialog take over: while the agent is up, a slip it failed (paper out) is reported, never sent to a
 * second printer, because a timed-out slip may already be on paper and would come out twice.
 */
export async function printJobs(baseUrl: string | null, jobs: PrintJob[]): Promise<PrintOutcome> {
  preparationError = null;
  const total = jobs.length;
  if (!total) return { via: "none", printed: 0, total };
  // Browser fallback is allowed only before any print POST has started.
  if (baseUrl && await agentOnline(baseUrl)) {
    let printed = 0;
    for (const job of jobs) if (await sendToAgent(baseUrl, job)) printed++;
    return { via: "agent", printed, total };
  }
  if (!browserPrinter) return { via: "none", printed: 0, total };
  try { await browserPrinter(jobs); }
  catch (err) { preparationError = (err as Error).message; return { via: "none", printed: 0, total }; }
  return { via: "browser", printed: total, total };
}

/** What to tell the cashier when not every slip printed; null when all did. */
export function printProblem(o: PrintOutcome): string | null {
  if (o.printed === o.total) return null;
  if (preparationError) return preparationError;
  return o.printed ? `Printed ${o.printed} of ${o.total} slips` : "The printer did not respond";
}

/**
 * Tell the server about a duplicate or audit slip printed here (roll reconciliation and duplicate forensics, SOW
 * Modules 4 and 10). Telemetry: a failure must never hold up the counter, so it is not shown to the cashier.
 */
export function logPrint(kind: "duplicate" | "slip", receiptId?: string): void {
  api.post("/api/counters/print-log", { kind, receiptId }).catch(() => undefined);
}
